import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from "recharts";
import {
  CheckCircle2, Wallet, FileClock, CalendarClock, TrendingUp, ArrowRight, Boxes, Receipt,
} from "lucide-react";
import api, { cachedGet } from "@/lib/api";
import { euro, euro0, fmtDate, fmtDateTime, STATUS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import IncomeReport from "@/components/IncomeReport";

const KPI = ({ icon: Icon, label, value, sub, tone, testid }) => (
  <div className="rounded-xl border border-border bg-card p-5 hover:shadow-md transition-shadow duration-200 animate-in-up" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${tone}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div className="mt-4 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">{value}</div>
    <div className="text-sm font-medium text-foreground mt-0.5">{label}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

// Numeri, grafici e liste della vecchia panoramica (PC e pagina «Pagamenti e incassi»).
export const DashboardStats = () => {
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [appts, setAppts] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    Promise.all([cachedGet("/stats"), cachedGet("/quotes"), cachedGet("/appointments")]).then(
      ([s, q, a]) => {
        setStats(s.data);
        setQuotes(q.data.slice(0, 5));
        setAppts(a.data.filter((x) => new Date(x.data) >= new Date()).slice(0, 4));
      }
    );
  }, []);

  if (!stats) return <div className="text-muted-foreground">Caricamento…</div>;

  const statusData = [
    { name: "Bozza", value: stats.status_breakdown.bozza, color: "#94a3b8" },
    { name: "Inviato", value: stats.status_breakdown.inviato, color: "#f59e0b" },
    { name: "Accettato", value: stats.status_breakdown.accettato, color: "#10b981" },
    { name: "Rifiutato", value: stats.status_breakdown.rifiutato, color: "#f43f5e" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-stats">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPI icon={CheckCircle2} label="Preventivi Accettati" value={stats.accepted_count}
          sub={`${euro0(stats.accepted_revenue)} di valore`} tone="bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400" testid="kpi-accepted-quotes-count" />
        <KPI icon={Wallet} label="Incassi (accettati)" value={euro0(stats.accepted_revenue)}
          sub="Totale lavori confermati" tone="bg-primary/10 text-primary" testid="kpi-accepted-revenue-total" />
        <KPI icon={FileClock} label="Preventivi in Attesa" value={stats.pending_count}
          sub={`${euro0(stats.pending_revenue)} potenziali`} tone="bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400" testid="kpi-pending-quotes-count" />
        <KPI icon={CalendarClock} label="Scadenze (7 gg)" value={stats.upcoming_count}
          sub="Scadenze e appuntamenti" tone="bg-indigo-50 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400" testid="kpi-upcoming-deadlines-count" />
      </div>

      {isAdmin && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="dashboard-profit-strip">
          <KPI icon={Wallet} label="Incassato (fatture)" value={euro0(stats.incassato_totale || 0)}
            sub="Rate e fatture pagate" tone="bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400" testid="kpi-incassato-totale" />
          <KPI icon={Boxes} label="Costi materiali" value={euro0(stats.materiali_speso || 0)}
            sub="Spese sui lavori/fatture" tone="bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400" testid="kpi-materiali-speso" />
          <KPI icon={Receipt} label="Spese di gestione" value={euro0(stats.spese_gestione || 0)}
            sub="Benzina, assicurazioni…" tone="bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400" testid="kpi-spese-gestione" />
          <KPI icon={TrendingUp} label="Utile netto" value={euro0(stats.utile_netto || 0)}
            sub="Incassato − tutte le uscite" tone="bg-primary/10 text-primary" testid="kpi-utile-netto" />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-semibold">Andamento incassi (6 mesi)</h3>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={stats.monthly} margin={{ left: -10, right: 8, top: 8 }}>
              <defs>
                <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#EA580C" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#EA580C" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gPen" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="mese" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `€${v / 1000}k`} />
              <Tooltip formatter={(v) => euro(v)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 13 }} />
              <Area type="monotone" dataKey="incassi" name="Incassato" stroke="#EA580C" strokeWidth={2.5} fill="url(#gInc)" />
              <Area type="monotone" dataKey="attesa" name="In attesa" stroke="#f59e0b" strokeWidth={2} fill="url(#gPen)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-heading font-semibold mb-4">Stato preventivi</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={statusData} margin={{ left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 13 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Totale preventivi</span><span className="font-semibold">{stats.total_quotes}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Clienti in rubrica</span><span className="font-semibold">{stats.total_contacts}</span></div>
          </div>
        </div>
      </div>

      {isAdmin && <IncomeReport />}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-border">
            <h3 className="font-heading font-semibold">Preventivi recenti</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/preventivi")} className="text-primary">
              Vedi tutti <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="divide-y divide-border">
            {quotes.map((q) => (
              <div key={q.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-secondary/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{q.cliente_nome}</div>
                  <div className="text-xs text-muted-foreground">N. {q.numero} · {fmtDate(q.data_emissione)}</div>
                </div>
                <div className="font-semibold text-sm">{euro(q.totale)}</div>
                <Badge variant="outline" className={STATUS[q.stato]?.cls}>{STATUS[q.stato]?.label}</Badge>
              </div>
            ))}
            {quotes.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">Nessun preventivo.</div>}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-border">
            <h3 className="font-heading font-semibold">Prossimi appuntamenti</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/agenda")} className="text-primary">
              Agenda <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="divide-y divide-border">
            {appts.map((a) => (
              <div key={a.id} className="px-5 py-3.5">
                <div className="font-medium text-sm truncate">{a.titolo}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{a.cliente_nome}</div>
                <div className="text-xs text-primary font-medium mt-1">{fmtDateTime(a.data)}</div>
              </div>
            ))}
            {appts.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">Nessun appuntamento.</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardStats;
